/**
 * Colours for aircraft track lines.
 *
 * The previous palette was twelve saturated hues picked at RANDOM per track,
 * including #ff0000 and #00ff00. Two tracks drawn together could land on red
 * and green — indistinguishable to a red/green colourblind reader — or on the
 * same colour twice.
 *
 * These are ordered so that each entry differs from its neighbour in
 * LUMINANCE, which survives any colour vision deficiency, and are assigned by
 * index rather than at random so consecutive tracks cannot collide.
 */
export const AFF_TRACK_COLORS = [
  '#ffffff', // white — brightest (1.00)
  '#0091ea', // strong blue — dark (0.26)
  '#ffd400', // amber — bright (0.68)
  '#a355c7', // orchid — dark (0.18), still readable on the dark basemap
  '#4dd0e1', // light cyan — bright (0.52)
  '#c62828', // deep red — dark (0.14); no pure green anywhere in the list
  '#f5b7d0', // pale pink — bright (0.58)
  '#00a693', // teal — mid (0.29)
] as const;

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function relativeLuminance(hex: string): number {
  const clean = hex.replace('#', '');
  const channel = (offset: number): number => {
    const v = parseInt(clean.slice(offset, offset + 2), 16) / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(0) + 0.7152 * channel(2) + 0.0722 * channel(4);
}

/** Deterministic colour for the Nth track currently on the map. */
export const trackColorForIndex = (index: number): string =>
  AFF_TRACK_COLORS[((index % AFF_TRACK_COLORS.length) + AFF_TRACK_COLORS.length) % AFF_TRACK_COLORS.length];

/**
 * Colour for a new track: the first in the palette that no drawn track is using.
 *
 * Indexing by the number of drawn tracks reused a colour after a dismissal —
 * draw two, dismiss the first, and the next took the second's colour (#866).
 * Only once every colour is on the map does it cycle.
 */
export const nextTrackColor = (inUse: readonly string[]): string =>
  AFF_TRACK_COLORS.find((c) => !inUse.includes(c)) ?? trackColorForIndex(inUse.length);

/**
 * Halo (drop-shadow) colours drawn beneath a track line.
 *
 * Mapbox has no shadow property on line layers, so the shadow is a wider,
 * blurred casing underneath. A single fixed halo colour cannot work on both
 * basemaps — a dark halo disappears on a dark map, a light one on a light map.
 * Choosing the halo against the TRACK's luminance guarantees that whichever
 * basemap is in use, one of the pair contrasts with it.
 */
export const TRACK_HALO_DARK = '#000000';
export const TRACK_HALO_LIGHT = '#ffffff';

/** Luminance above which a track counts as bright and takes a dark halo. */
const HALO_PIVOT = 0.4;

export const haloColorFor = (trackColor: string): string =>
  relativeLuminance(trackColor) > HALO_PIVOT ? TRACK_HALO_DARK : TRACK_HALO_LIGHT;

/** Width multiplier and blur for the halo, relative to the track line. */
export const TRACK_LINE_WIDTH = 2;
export const TRACK_HALO_WIDTH = 6;
export const TRACK_HALO_BLUR = 3;
export const TRACK_HALO_OPACITY = 0.65;
